import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { get, qs } from "../../api/client";
import type { Contact, Page } from "../../api/types";
import { formatPhone } from "../../lib/format";

export const SUGGESTION_LIST_ID = "dial-suggestions";
export const suggestionId = (i: number) => `dial-suggestion-${i}`;
const MAX = 3;

const digitsOf = (s: string) => s.replace(/\D/g, "");

/**
 * Contacts whose number contains what's been dialed so far (3+ digits, debounced). Searching happens on
 * the server, so it works for any size of contact list and respects contacts.read.
 */
export function useContactSuggestions(number: string, enabled: boolean): Contact[] {
  const digits = digitsOf(number);
  const [query, setQuery] = useState(digits);
  useEffect(() => {
    const t = setTimeout(() => setQuery(digits), 200);
    return () => clearTimeout(t);
  }, [digits]);
  const active = enabled && query.length >= 3 && digits.length >= 3;
  const q = useQuery({
    queryKey: ["contacts", "dial-suggestions", query],
    queryFn: () => get<Page<Contact>>(`/contacts${qs({ search: query, pageSize: MAX + 1 })}`),
    enabled: active,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
  if (!active || !q.data) return [];
  // Hide once the number is exactly a contact's (nothing left to suggest), and drop stale results.
  const items = q.data.items.filter((c) => digitsOf(c.phone).includes(digits));
  // A full number typed without its country code (e.g. 10 US digits) counts as an exact match too.
  if (items.some((c) => digitsOf(c.phone) === digits || (digits.length >= 10 && digitsOf(c.phone).endsWith(digits)))) return [];
  return items.slice(0, MAX);
}

/** Bolds the dialed digits inside the formatted number. */
function Highlighted({ phone, digits }: { phone: string; digits: string }) {
  const shown = formatPhone(phone);
  // Map each digit of the formatted string back to its position among digits only.
  const positions: number[] = [];
  for (let i = 0; i < shown.length; i++) if (/\d/.test(shown[i]!)) positions.push(i);
  const start = digitsOf(shown).indexOf(digits);
  if (start < 0) return <>{shown}</>;
  const from = positions[start]!;
  const to = positions[start + digits.length - 1]! + 1;
  return <>{shown.slice(0, from)}<strong className="font-semibold text-default">{shown.slice(from, to)}</strong>{shown.slice(to)}</>;
}

export function ContactSuggestions({ items, number, activeIndex, onPick }: { items: Contact[]; number: string; activeIndex: number; onPick: (c: Contact) => void }) {
  if (items.length === 0) return null;
  const digits = digitsOf(number);
  return (
    <ul id={SUGGESTION_LIST_ID} role="listbox" aria-label="Matching contacts" className="overflow-hidden rounded-2xl bg-white/55 ring-1 ring-inset ring-white/70">
      {items.map((c, i) => (
        <li
          key={c.id}
          id={suggestionId(i)}
          role="option"
          aria-selected={i === activeIndex}
          // mousedown, not click: keeps focus in the number field.
          onMouseDown={(e) => { e.preventDefault(); onPick(c); }}
          className={`flex cursor-pointer items-center gap-3 px-3 py-2 transition ${i === activeIndex ? "bg-accent-500/10" : "hover:bg-white/80"} ${i > 0 ? "border-t border-white/70" : ""}`}
        >
          <span className="bg-accent-gradient grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-semibold text-white" aria-hidden="true">
            {c.name.slice(0, 1).toUpperCase()}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-default">{c.name}</span>
            <span className="block truncate text-xs text-muted tabular"><Highlighted phone={c.phone} digits={digits} />{c.company ? ` · ${c.company}` : ""}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}
